import React, { useEffect } from 'react';
import { EVBox } from '@eagleview/ev-comp-library';
import Loader from 'commonComponents/Loader';

/**
 * LogoutHandler Component - Handles logout redirect and generates final redirect URL
 *
 * This component is used as an intermediate step during the logout process for first-time users.
 * It retrieves onboarding details stored in localStorage before logout, constructs the final
 * redirect URL to the signup service with proper parameters, and redirects the user.
 *
 * Flow:
 * 1. User signs out from /start page with onboarding details
 * 2. Okta redirects to /logout-handler after logout
 * 3. This component constructs the final URL with stored details
 * 4. User is redirected to the signup service with proper parameters
 */
const LogoutHandler = () => {
  // Run the redirect logic immediately
  useEffect(() => {
    const handleLogoutRedirect = () => {
      try {
        // Get stored onboarding details from localStorage
        const onboardingDetails = localStorage.getItem('ev_onboarding_details');

        if (onboardingDetails) {
          const details = JSON.parse(onboardingDetails);

          // Clean up the stored details
          localStorage.removeItem('ev_onboarding_details');

          // Construct the final redirect URL
          const baseUrl = `${process.env.REACT_APP_ACCOUNT_ONBOARDING_URL}/signin`;
          const params = new URLSearchParams();

          // Add required parameters
          const { clientId } = details;
          if (clientId) {
            params.set('client_id', clientId);
          }
          const { is3legged } = details;
          params.set('response_type', 'code');
          params.set('scope', 'openid offline_access');
          params.set('is3legged', is3legged);
          params.set('signup', 'true');
          params.set('fromActivation', 'true');
          if (is3legged === 'true') {
            const { partnerName, partnerUrl } = details;
            params.set('partner_name', partnerName);
            params.set('partner_url', partnerUrl);
          }

          // Generate and store encrypted state parameter following app's pattern
          const { stateData } = details;
          // Use stateData if available (for 3-legged flow), otherwise use default
          const stateValue = `state-${stateData}`;
          const stateHash = window.btoa(stateValue);
          // Store both the hash and the route for validation (matching getSubRoute pattern)
          sessionStorage.setItem(stateHash, stateData);

          // Add redirect_uri - this should be the authorize callback
          const { onboardingAppLink } = details;

          // Construct final URL
          const finalUrl = `${baseUrl}?${params.toString()}&redirect_uri=${onboardingAppLink}&state=${stateHash}`;

          // Redirect to the final URL
          window.location.assign(finalUrl);
        } else {
          // If no onboarding details found, redirect to home
          window.location.assign('/');
        }
      } catch (error) {
        // Fallback redirect to home
        window.location.assign('/');
      }
    };

    handleLogoutRedirect();
  }, []);

  return (
    <EVBox
      position="fixed"
      top={0}
      left={0}
      width="100vw"
      height="100vh"
      display="flex"
      justifyContent="center"
      alignItems="center"
      bgcolor="white"
      zIndex={9999}
    >
      <Loader />
    </EVBox>
  );
};

export default LogoutHandler;
